import { Component, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BreadCrumb } from '@nationwide/internet-servicing-angular-components';
import { FormErrorMapperService } from 'app/shared/services/form-error-mapper.service';
import { environment } from 'environments/environment';
import { ANALYTICS, COMPONENT_NAME } from '../shared/constants/claims.constants';
import { Analytics } from '../shared/models/analytics.model';
import { BaseComponent } from '../shared/models/base-component';
import { EnvironmentpickerService } from '../shared/services/environmentpicker.service';
import { LoggerService } from '../shared/services/logger.service';
import { RepairShopAddress, RepairShopService, VehicleMake } from '../shared/services/repair-shop.service';

interface VehicleOption {
  caption: string;
  value: string;
}

@Component({
  selector: 'find-repair-shop',
  templateUrl: './find-repair-shop.component.html',
  styleUrls: ['./find-repair-shop.component.scss'],
  standalone: false
})
export class FindRepairShopComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() accessToken!: string;
  @Input() addressLoaded = false;
  @Input() claimId?: string;
  @Input() externalClaimId?: string;
  @Input() vehicleIncidentId?: string;
  @Input() clientId: string = environment.appProps.appClientId;
  @Input() currentLocation!: string;
  @Input() defaultSearchLocation!: RepairShopAddress;
  @Input() env!: string;
  @Input() fnolVehicleYear = '';
  @Input() fnolVehicleMake = '';
  @Input() fnolVehicleModel = '';
  @Output() analytics = new EventEmitter<Analytics>();
  @Output() data = new EventEmitter<any>();
  @Output() onError = new EventEmitter<any>();
  @Input() showBreadCrumbs = true;
  @Input() startTime = performance.now();
  @Input() userType!: string;

  breadCrumbs: BreadCrumb[] = [];
  homeIconLink = environment.NATIONWIDE.HOME;
  currentPage = 'Find a repair shop';

  isAuthenticated = false;
  readonly userTypeArray: string[] = ['registered', 'unregistered-member', 'unregistered-member-support'];
  readonly startYear = 1981;
  selectedYear = '';
  selectedMake = '';
  selectedModel = '';
  vehicleYearList: VehicleOption[] = [];
  vehicleMakeList: VehicleOption[] = [];
  vehicleModelList: VehicleOption[] = [];
  vehicleLookupMessage = '';
  vehicleForm!: FormGroup;

  constructor(
    private environmentpickerService: EnvironmentpickerService,
    private repairShopService: RepairShopService,
    private logger: LoggerService,
    private formBuilder: FormBuilder,
    public formError: FormErrorMapperService
  ) {
    super();
  }

   @HostListener('window:beforeunload', ['$event'])
    unloadHandler(event): any {
        this.logger.logCurrentContents();
    }

  handleAddressLoaded(isLoaded: boolean): void {
    this.addressLoaded = isLoaded;
    const endTime = performance.now();
    this.analytics.emit({
      name: `${ANALYTICS.LOAD_TIME}: ${COMPONENT_NAME.SEARCH_BAR}`,
      value: {
        loadTime: endTime - this.startTime,
        authenticated: this.isAuthenticated
      }
    });
  }

  handleError(error: any): void {
    this.onError.emit(error);
  }

  setBreadCrumbs(): void {
    this.homeIconLink = environment.NATIONWIDE.HOME;
    this.breadCrumbs = [
      {
        name: 'Claims Center',
        link: environment.NATIONWIDE.CLAIM_CENTER_URL
      }
    ];
  }

  ngOnInit(): void {
    if (!this.env) {
      this.env = environment.CLAIMS_DEV ? this.environmentpickerService.getEnvironmentCookie() : 'prod';
    }
    this.setBreadCrumbs();
    this.isAuthenticated = this.userTypeArray.includes(this.userType);
    this.vehicleYearList = this.getVehicleYearList();
    this.initializeForm();
    this.syncFnolVehicleSelection();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const fnolInputsChanged = !!(changes['fnolVehicleYear'] || changes['fnolVehicleMake'] || changes['fnolVehicleModel']);
    if (!fnolInputsChanged || !this.vehicleForm) {
      return;
    }
    this.syncFnolVehicleSelection();
  }

  initializeForm(): void {
    this.vehicleForm = this.formBuilder.group({
      year: ['', Validators.required],
      make: ['', Validators.required],
      model: ['', Validators.required]
    });
  }

  getVehicleYearList(): VehicleOption[] {
    const currentYearPlusOne = new Date().getFullYear() + 1;
    const years: VehicleOption[] = [];

    for (let year = currentYearPlusOne; year >= this.startYear; year--) {
      years.push({
        caption: String(year),
        value: String(year)
      });
    }

    return years;
  }

  onYearChange(event: Event): void {
    const target = event?.target as HTMLSelectElement;
    this.selectedYear = target?.value || '';
    this.vehicleForm.patchValue({ year: this.selectedYear });
    this.selectedMake = '';
    this.selectedModel = '';
    this.vehicleForm.patchValue({ make: '', model: '' });
    this.vehicleMakeList = [];
    this.vehicleModelList = [];
    this.vehicleLookupMessage = '';

    if (!this.selectedYear) {
      return;
    }

    this.repairShopService.getVehicleMakes(this.selectedYear).subscribe({
      next: (response: any) => {
        const vehicleMakes: VehicleMake[] = response?.retrieveVehicleModelMakesResponse?.vehicleMake || [];
        this.vehicleMakeList = vehicleMakes
          .filter((item: VehicleMake) => !!item?.make && !!item?.makeDescription)
          .map((item: VehicleMake) => ({
            value: item.make,
            caption: item.makeDescription
          }));
      },
      error: (error: any) => {
        this.vehicleMakeList = [];
        if (error?.status === 404) {
          this.vehicleLookupMessage = 'No make data was found for the selected year.';
          return;
        }
        this.logger.error('FindRepairShopComponent - getVehicleMakes failed', error);
        this.onError.emit(error);
      }
    });
  }

  onMakeChange(event: Event): void {
    const target = event?.target as HTMLSelectElement;
    this.selectedMake = target?.value || '';
    this.vehicleForm.patchValue({ make: this.selectedMake });
    this.selectedModel = '';
    this.vehicleForm.patchValue({ model: '' });
    this.vehicleModelList = [];
    this.vehicleLookupMessage = '';

    if (!this.selectedYear || !this.selectedMake) {
      return;
    }

    this.repairShopService.getVehicleModels(this.selectedYear, this.selectedMake).subscribe({
      next: (response: any) => {
        const rawModels = response?.retrieveVehicleModelsResponse?.model;
        if (!Array.isArray(rawModels)) {
          this.vehicleModelList = [];
          this.vehicleLookupMessage = 'Model data is temporarily unavailable. Please try again.';
          this.logger.error('FindRepairShopComponent - getVehicleModels returned invalid payload', response);
          this.onError.emit({ errorMessage: 'Invalid model response payload', response });
          return;
        }

        const models: string[] = rawModels;
        this.vehicleModelList = models
          .filter((item: string) => !!item)
          .map((item: string) => ({
            value: item,
            caption: item
          }));

        if (this.vehicleModelList.length === 0) {
          this.vehicleLookupMessage = 'No model data was found for the selected year and make.';
        }
      },
      error: (error: any) => {
        this.vehicleModelList = [];
        const statusCode = this.getErrorStatusCode(error);
        if (statusCode === 404) {
          this.vehicleLookupMessage = 'No model data was found for the selected year and make.';
          return;
        }
        this.logger.error('FindRepairShopComponent - getVehicleModels failed', error);
        this.onError.emit(error);
      }
    });
  }

  onModelChange(event: Event): void {
    const target = event?.target as HTMLSelectElement;
    this.selectedModel = target?.value || '';
    this.vehicleForm.patchValue({ model: this.selectedModel });
  }

  private syncFnolVehicleSelection(): void {
    const fnolYear = (this.fnolVehicleYear || '').trim();
    const fnolMake = (this.fnolVehicleMake || '').trim();
    const fnolModel = (this.fnolVehicleModel || '').trim();

    if (!fnolYear && !fnolMake && !fnolModel) {
      return;
    }

    this.selectedYear = fnolYear;
    this.selectedMake = fnolMake;
    this.selectedModel = fnolModel;
    this.vehicleForm.patchValue({
      year: this.selectedYear,
      make: this.selectedMake,
      model: this.selectedModel
    });
  }

  setShopData(shop: any): void {
    this.data.emit(shop);
  }

  private getErrorStatusCode(error: any): number | undefined {
    return error?.status || error?.error?.status || error?.error?.statusCode;
  }
}
